import type { StatusLevel } from './StatusPill';

export type FFTThumbnail = {
  at: Date;
  bins: number[];
  severity: StatusLevel;
};

export type FFTThumbnailStripProps = {
  thumbnails: FFTThumbnail[];
  selectedAt: Date;
  onSelect: (at: Date) => void;
};

const BAR_FILL: Record<StatusLevel, string> = {
  critical: 'fill-critical',
  warn: 'fill-warn',
  watch: 'fill-watch',
  healthy: 'fill-healthy',
  info: 'fill-info',
};

function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Strip of mini-FFT thumbnails over a time window. Clicking a thumbnail
 * sets the LinkedCursor's `t*`. Selected thumbnail picks up an `info`
 * outline; severity tint comes from the per-thumbnail severity prop.
 *
 * Order: newest first (left-most). Operators scan from "right now"
 * backward in time, so the latest capture wants the prime left-edge
 * position. Callers can pass thumbnails in any order; we sort here.
 */
export function FFTThumbnailStrip({
  thumbnails,
  selectedAt,
  onSelect,
}: FFTThumbnailStripProps) {
  const selectedTime = selectedAt.getTime();
  const ordered = [...thumbnails].sort(
    (a, b) => b.at.getTime() - a.at.getTime(),
  );
  return (
    <div
      role="radiogroup"
      aria-label="FFT thumbnails by time"
      className="flex gap-sp-2 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50 p-sp-2"
    >
      {ordered.map((thumb) => {
        const selected = thumb.at.getTime() === selectedTime;
        const max = Math.max(...thumb.bins, 1);
        return (
          <button
            key={thumb.at.toISOString()}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`FFT at ${formatTime(thumb.at)}, severity ${thumb.severity}`}
            onClick={() => onSelect(thumb.at)}
            className={
              'flex shrink-0 flex-col items-center gap-sp-1 rounded-r-default border p-sp-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
              (selected
                ? 'border-info bg-info-bg'
                : 'border-gray-300 bg-gray-50 hover:border-gray-500')
            }
          >
            <svg
              viewBox="0 0 64 36"
              width="64"
              height="36"
              aria-hidden
            >
              {thumb.bins.map((v, i) => {
                const x = (i / thumb.bins.length) * 64;
                const w = 64 / thumb.bins.length - 0.5;
                const h = Math.max(1, (v / max) * 34);
                return (
                  <rect
                    key={i}
                    x={x}
                    y={36 - h}
                    width={w}
                    height={h}
                    className={BAR_FILL[thumb.severity]}
                  />
                );
              })}
            </svg>
            <span className="font-mono text-fs-meta text-gray-700">
              {formatTime(thumb.at)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
